/**
 * The shell screens (header, sign-in gate, no-access, problem), rendered with lit-html so every interpolated value is escaped (Phase 2 will render
 * employee names and free text; the escaping discipline starts here). Markup and class names follow MOWCTX Home
 * (header.top, .wrap, .card, .btn) so the app reads as a sibling of Home.
 */
import { html, nothing, type TemplateResult } from 'lit-html';
import type { Me } from './api';
import { initials } from './format';

const logo = html`<img class="logo" src="/assets/logo-white.png" alt="Meals on Wheels Central Texas">`;

export function header(opts: { me?: Me; onSignOut?: () => void; onTheme: () => void }): TemplateResult {
  return html`
    <header class="top">
      <div class="wrap">
        ${logo}
        <a class="app-name" href="#/">MOW Lifecycle</a>
        <div class="me">
          <button class="theme" type="button" aria-label="Switch light / dark" title="Switch light / dark" @click=${opts.onTheme}>
            <svg class="i moon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z"/></svg>
            <svg class="i sun" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"/></svg>
          </button>
          ${opts.me
            ? html`${opts.me.access.canAdmin ? html`<a class="link-btn" href="#/admin">Admin</a>` : nothing}
                <span class="who"><b>${opts.me.name}</b><span>${opts.me.upn}</span></span>
                <span class="avatar" aria-hidden="true">${initials(opts.me.name)}</span>
                <button class="link-btn" type="button" @click=${opts.onSignOut}>Sign out</button>`
            : nothing}
        </div>
      </div>
    </header>`;
}

export function signedOut(onSignIn: () => void, busy: boolean): TemplateResult {
  return html`
    <main id="main" class="gate">
      <div class="card gate-card">
        <div class="gh">${logo}</div>
        <h1>MOW Lifecycle</h1>
        <p>Onboarding and offboarding requests for Meals on Wheels Central Texas managers.</p>
        <button class="btn" type="button" ?disabled=${busy} @click=${onSignIn}>${busy ? 'Signing you in…' : 'Sign in with your MOWCTX account'}</button>
      </div>
    </main>`;
}

export function noAccess(upn: string, onSignOut: () => void): TemplateResult {
  return html`
    <main id="main" class="gate">
      <div class="card gate-card">
        <div class="gh">${logo}</div>
        <h1>You don't have access to MOW Lifecycle</h1>
        <p>You are signed in as <b>${upn}</b>, but that account is not in the MOW Lifecycle Managers group.</p>
        <p>If you manage staff or volunteers and need access, ask Business Operations to add you.</p>
        <button class="btn" type="button" @click=${onSignOut}>Sign out</button>
      </div>
    </main>`;
}

export function problem(message: string, onRetry: () => void): TemplateResult {
  return html`
    <main id="main" class="gate">
      <div class="card gate-card">
        <div class="gh">${logo}</div>
        <h1>Something went wrong</h1>
        <p class="err">${message}</p>
        <button class="btn" type="button" @click=${onRetry}>Try again</button>
      </div>
    </main>`;
}
